import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Tabs as EbscoTabs } from '@ebsco-ui/ebsco-ui';

import { getTabsI18n } from '@app/utils';
import { Route, ShapeWithClassName } from '@app/constants';

interface Tab {
  title: string;
  component: ReactElement;
  route: string;
}

interface TabsWithRoutingProps {
  tabs: ShapeWithClassName<Tab>[];
  parentRoute: Route;
}

export const TabsWithRouting: FC<ShapeWithClassName<TabsWithRoutingProps>> = ({
  tabs,
  parentRoute,
  className,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isParentRootRoute = useMemo(
    () => matchPath(pathname, parentRoute),
    [parentRoute, pathname]
  );
  const isParentDescendantRoute = useMemo(
    () => tabs.some(tab => tab.route === pathname),
    [pathname, tabs]
  );

  const handleTabSelection = useCallback(
    (index: number) => navigate(tabs[index].route),
    [navigate, tabs]
  );

  const getSelectedTabIndex = () => {
    const tabIndex = tabs.findIndex(({ route }) => route === pathname);

    return tabIndex > -1 ? tabIndex : 0;
  };

  if (isParentRootRoute) {
    return <Navigate replace to={tabs[0].route} />;
  }

  if (!isParentDescendantRoute) {
    return <Navigate replace to={Route.notFound} />;
  }

  return (
    <EbscoTabs
      selectedIndex={getSelectedTabIndex()}
      i18n={getTabsI18n(intl)}
      tabs={tabs}
      className={className}
      onSelect={handleTabSelection}
    >
      {tabs.map((tab, index) => (
        <EbscoTabs.TabPanel key={index}>{tab.component}</EbscoTabs.TabPanel>
      ))}
    </EbscoTabs>
  );
};
